import { Epic, combineEpics } from 'redux-observable';
import {
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil,
  withLatestFrom,
  zip,
} from 'rxjs';

import * as api from './custom_sizes.api';

import activeEditor from 'domains/active_editor';
import canvases from 'domains/canvases';
import products from 'domains/products';
import ogs from 'domains/option_groups';

import { notNull } from 'utils/type_guards';
import { byId, catchAndLogError } from 'utils/common';

import type { Action } from '@reduxjs/toolkit';
import type { RootState } from 'store/wos_store';
import type { OptionalPair } from 'types/common_types';

/**
 * When the custom size changes, we need to:
 * - validate the size
 * - check custom options
 * - fetch prices
 *
 * Debounced as we're allowing the size to change as much as the user wants.
 *
 * Errors in either validation or fetching price are passed through for the slice
 * reducers to handle
 */
export const customSizeChangeEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    withLatestFrom(state$),
    map(([, state]) => {
      const product = activeEditor.selectors.product(state);
      const optionGroups = activeEditor.selectors.optionGroups(state);
      const configuredOptions = activeEditor.selectors.optionConfig(state);
      if (!product || !products.utils.isCustomSizeProduct(product) || optionGroups.length === 0)
        return null;

      const size = activeEditor.selectors.customSize(state);

      return {
        size,
        customOptions: product.custom_options,
        billingCode: product.billing_code,
        optionGroups,
        configuredOptions,
      };
    }),
    filter(notNull),
    distinctUntilChanged((prev, current) => {
      return prev.size.height === current.size.height && prev.size.width === current.size.width;
    }),
    debounceTime(1000),
    switchMap((payload) => {
      // validate size, throw error to prevent price requests if invalid
      const { size, customOptions } = payload;
      const valid = products.utils.validateCustomSize(size.width, size.height, customOptions);

      if (!valid.valid) {
        return of(canvases.actions.calculateCustomPrice.rejected(valid.reason));
      }

      const { billingCode, optionGroups, configuredOptions } = payload;
      return from([
        canvases.actions.calculateCustomPrice.pending({ size, billingCode }),
        canvases.actions.calculateCustomOptionPrices.pending({
          size,
          optionGroups,
          configuredOptions,
        }),
      ]);
    })
  );

/**
 * Fetch the product price for this custom size
 */
export const customSizeProductPriceEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(canvases.actions.calculateCustomPrice.pending.match),
    switchMap(({ payload }) => {
      const { size, billingCode } = payload;
      return from(api.getCustomSizePrice(size.width, size.height, billingCode)).pipe(
        map((response) =>
          canvases.actions.calculateCustomPrice.fulfilled(response.cost.toFixed(2))
        ),
        catchAndLogError(canvases.actions.calculateCustomPrice.rejected())
      );
    })
  );

/**
 * Enable/disable any options based on the custom size change
 */
const customSizeOptionsEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(canvases.actions.calculateCustomOptionPrices.pending.match),
    map(({ payload }) => {
      const { size, optionGroups, configuredOptions } = payload;

      const disabledChanges: OptionalPair<number, number>[] = Object.entries(configuredOptions)
        // Collect any options that need disabled at this new size
        .map(([ogid, oid]) => {
          const og = optionGroups.find(byId(parseInt(ogid)));
          if (!og || !oid) return null;

          const o = og.options.find(byId(oid));
          if (!o || !o.custom_options) return null;

          if (!ogs.utils.customSizeOptionIsDisabled(size.height, size.width, o.custom_options))
            return null;

          return { og, o };
        })
        .filter(notNull)
        // Handle selecting new options in place of the disabled option
        .map(({ og, o }) => {
          const isCrateOrFreight = ogs.utils.isCrateOrFreightOptionGroup(og);
          if (og.required && !isCrateOrFreight) {
            const oid = ogs.utils.findDefaultOptionForCustomSize(og, size);
            return [og.id, oid];
          } else if (isCrateOrFreight) {
            const matchingCrateOption = ogs.utils.findDefaultCrateOptionForCustomSize(og, size);
            return [og.id, matchingCrateOption];
          } else {
            return [og.id, undefined];
          }
        });

      return disabledChanges;
    }),
    map((options) => canvases.actions.changeActiveCanvasConfig.options({ options }))
  );

/**
 * Fetch option prices for custom sizes after both actions for size change & disabled option check
 */
const customSizeOptionsPriceEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(canvases.actions.calculateCustomOptionPrices.pending.match),
    switchMap(({ payload }) =>
      action$.pipe(
        filter(canvases.actions.changeActiveCanvasConfig.options.match),
        takeUntil(action$.pipe(filter(canvases.actions.changeActiveCanvasConfig.product.match))),
        withLatestFrom(state$),
        mergeMap(([_, state]) => {
          const { size, optionGroups } = payload;
          const configuredOptions = activeEditor.selectors.optionConfig(state);

          const calculatedPriceOptions = ogs.utils
            .findCustomSizeOptionsWithCalculatedPrice(optionGroups, configuredOptions)
            .map(([ogid, bc]) => {
              return zip([
                of(ogid),
                from(api.getCustomSizePrice(size.width, size.height, bc)).pipe(
                  map((priceData) => priceData.cost)
                ),
              ]);
            });

          return calculatedPriceOptions.length > 0 ? forkJoin(calculatedPriceOptions) : of([]);
        }),
        map(canvases.actions.calculateCustomOptionPrices.fulfilled),
        catchAndLogError(canvases.actions.calculateCustomOptionPrices.rejected())
      )
    )
  );

export default combineEpics(
  customSizeChangeEpic,
  customSizeProductPriceEpic,
  customSizeOptionsEpic,
  customSizeOptionsPriceEpic
);
